import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';

import { Store } from '@ngrx/store';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { NotificationTheme } from '@fcom/ui-components';
import { Category } from '@fcom/dapi/api/models/category';
import { TravelerService } from '@fcom/dapi/interfaces/services.interface';
import { SeatSelectionForFlight } from '@fcom/common/interfaces/seat-map.interface';

import { getSeatType } from '../../utils/ancillary.utils';
import { bundlesByCategorySelector } from '../../../../store/selectors/bundle.selector';
import { CommonBookingFeatureState } from '../../../../store/store.interface';
import { getBundleVariantCodes, isAncillaryBundle, isBundle, isSeatBundle } from '../../utils/category.utils';

const translationRoot = 'ancillaries.overlappingServicesNotification.';

const categoryTitleKey = (category: Category | undefined): string => `ancillaries.${category}.genericTitle`;

const upsellNotifications: OverlapNotificationType[] = [
  'convertServiceToSeatBundle',
  'convertMultipleServicesToBundle',
];

const sameVariants = (bundleVariants: string[], overlapVariants: string[]): boolean =>
  bundleVariants.length === overlapVariants.length &&
  bundleVariants.every((variant) => overlapVariants.includes(variant));

const namedSeat = (seatNumber: string, characteristics: string[]) => ({
  seatNumber,
  seatTypeKey: getSeatType(characteristics).label,
});

type OverlapNotificationType =
  | 'replaceServiceWithBundle'
  | 'replaceMultipleServicesWithBundle'
  | 'convertSeatBundleToSeatBundle'
  | 'removeServiceFromBundle'
  | 'removeServiceFromSeatBundle'
  | 'convertServiceToSeatBundle'
  | 'convertSeatBundleToSeat'
  | 'convertMultipleServicesToBundle';

@Component({
  selector: 'fin-service-overlap-notification',
  templateUrl: 'service-overlap-notification.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ServiceOverlapNotificationComponent {
  private store$ = inject<Store<CommonBookingFeatureState>>(Store);

  private bundlesByCategory = this.store$.selectSignal(bundlesByCategorySelector);

  category = input.required<Category>();
  overlappingServices = input.required<TravelerService[]>();
  bundleEligibleSeat = input<SeatSelectionForFlight>();
  fragmentId = input<string>();

  continueSelection = output<string | undefined>();
  cancelSelection = output<void>();

  private bundledServiceVariants = computed(
    () => this.bundlesByCategory()[this.category()]?.[this.fragmentId()]?.constituentVariants ?? []
  );

  private regularSeat = computed(() => {
    const seat = this.bundleEligibleSeat();

    return seat && namedSeat(seat.seatNumber, seat.characteristics);
  });

  // Only the seat number of this one is ever used; it is named for parity with the seat picked on the map.
  private bundleSeat = computed(() => {
    const seat = this.overlappingServices().find((service) => !!service.seatNumber);

    return seat && namedSeat(seat.seatNumber, getBundleVariantCodes(seat.variant));
  });

  // Never both: adding a bundle clears the regular seat selection and setting a seat clears the bundle ones.
  private existingSeat = computed(() => this.regularSeat() ?? this.bundleSeat());

  private notificationType = computed<OverlapNotificationType | undefined>(() => {
    const overlappingServices = this.overlappingServices();

    if (overlappingServices.length < 1) {
      return undefined;
    }

    if (this.category() === Category.SEAT) {
      return 'convertSeatBundleToSeat';
    }

    if (this.holdsEveryBundledService(overlappingServices)) {
      return 'convertMultipleServicesToBundle';
    }

    return overlappingServices.length === 1
      ? this.singleOverlapNotificationType(overlappingServices[0])
      : 'replaceMultipleServicesWithBundle';
  });

  readonly notificationData = computed(() => {
    const category = this.category();
    const overlappingServices = this.overlappingServices();
    const notificationType = this.notificationType();
    const isUpsell = upsellNotifications.includes(notificationType);
    const existingSeat = this.existingSeat();

    return {
      type: notificationType,
      messageKey: translationRoot + notificationType,
      currentCategoryKey: categoryTitleKey(category),
      ctaCategoryKey: categoryTitleKey(isSeatBundle(category) ? Category.SEAT : category),
      ctaTextKey: this.getCtaTextKey(notificationType, category, !!existingSeat),
      overlappingServices,
      overlapCategoryKey: categoryTitleKey(overlappingServices[0]?.parentCategory),
      existingSeat,
      theme: isUpsell ? NotificationTheme.SUCCESS : NotificationTheme.INFO,
      icon: isUpsell ? SvgLibraryIcon.DISCOUNTED_PRICE_OUTLINE : SvgLibraryIcon.INFO_OUTLINE,
    };
  });

  private holdsEveryBundledService(overlappingServices: TravelerService[]): boolean {
    if (isSeatBundle(this.category()) && !this.regularSeat()) {
      return false;
    }

    const bundledServiceVariants = this.bundledServiceVariants();

    return (
      bundledServiceVariants.length > 0 &&
      sameVariants(
        bundledServiceVariants,
        overlappingServices.map(({ variant }) => variant)
      )
    );
  }

  private singleOverlapNotificationType(overlap: TravelerService): OverlapNotificationType {
    const currentServiceIsSeatBundle = isSeatBundle(this.category());

    if (!isBundle(this.category()) && isBundle(overlap.parentCategory)) {
      return isSeatBundle(overlap.parentCategory) ? 'removeServiceFromSeatBundle' : 'removeServiceFromBundle';
    }

    if (isSeatBundle(overlap.parentCategory) && currentServiceIsSeatBundle) {
      return 'convertSeatBundleToSeatBundle';
    }

    return currentServiceIsSeatBundle && this.bundledServiceVariants().includes(overlap.variant)
      ? 'convertServiceToSeatBundle'
      : 'replaceServiceWithBundle';
  }

  private getCtaTextKey(
    notificationType: OverlapNotificationType,
    category: Category,
    hasExistingSeat: boolean
  ): string {
    if (notificationType === 'convertMultipleServicesToBundle') {
      return 'actions.confirm';
    }

    if (isAncillaryBundle(category) || (isSeatBundle(category) && hasExistingSeat)) {
      return translationRoot + 'switchToCombo';
    }

    return translationRoot + 'continueToSelect';
  }

  continueToSelection(preExistingSeatNumber: string | undefined): void {
    this.continueSelection.emit(preExistingSeatNumber);
  }
}
